import i18n, { use } from 'i18next'
import { initReactI18next } from 'react-i18next'
import translations, {
    DEFAULT_LANGUAGE,
    LANGUAGES,
    LanguageCode
} from '../translations'

const devEnvironment = process.env.NODE_ENV === 'development'

const LANGUAGE_STORAGE_KEY = 'expert.language'

const supportedLanguages = LANGUAGES.map(({ code }) => code)

const isSupportedLanguage = (value: unknown): value is LanguageCode =>
    typeof value === 'string' &&
    supportedLanguages.includes(value as LanguageCode)

/**
 * The default language is always English – only an explicit choice made by the
 * user (persisted in the browser) switches the application to another one.
 */
const getInitialLanguage = (): LanguageCode => {
    try {
        const storedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY)

        if (isSupportedLanguage(storedLanguage)) {
            return storedLanguage
        }
    } catch (error) {
        // Storage can be unavailable (private mode, blocked cookies) – ignore it.
    }

    return DEFAULT_LANGUAGE
}

const persistLanguage = (language: string) => {
    try {
        window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language)
    } catch (error) {
        // Persisting the choice is a convenience only, never a hard failure.
    }
}

use(initReactI18next).init({
    interpolation: { escapeValue: false },
    lng: getInitialLanguage(),
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: supportedLanguages,
    preload: supportedLanguages,
    initImmediate: true,
    react: {
        useSuspense: false
    },
    debug: devEnvironment,
    resources: translations
})

i18n.on('languageChanged', (language) => {
    persistLanguage(language)
    document.documentElement.setAttribute('lang', language)
})

document.documentElement.setAttribute('lang', i18n.language)

export default i18n
